<template>
  <div class="channel-three-news">
    <!-- 新闻列表：单栏 -->
    <div class="news-list">
      <div
        v-for="block in displayBlocks"
        :key="block.type + '-' + block.id"
        :class="['list-block', block.type]"
      >
        <!-- 横幅 -->
        <div
          v-if="block.type === 'banner' && bannerImg"
          class="banner-wrap"
          @click="goBannerLink" 
        >
          <img 
            class="banner-image"
            referrerpolicy="no-referrer"
            :src="bannerImg"
            alt="广告横幅"
          />
          <span v-if="bannerShowTag" class="ad-tag">广告</span>
        </div>

        <!-- 新闻 -->
        <div
          v-else
          class="news-item"
          :class="{ 'no-image': !block.item.img }"
        >
          <img
            v-if="block.item.img"
            class="news-thumb"
            referrerpolicy="no-referrer"
            :src="block.item.img || DEFAULT_IMAGE"
            :alt="block.item.title"
            @click="goDetail(block.item)"
            @error="handleImageError($event, block.item.img)"
            @load="handleImageLoad(block.item.id)"
            :class="{ 'img-loading': !block.item.loaded }"
          />
          <span v-if="!block.item.loaded && block.item.isLoading && block.item.img" class="img-placeholder">加载中...</span>
          <div class="news-content">
            <div class="news-title" @click="goDetail(block.item)">{{ block.item.title }}</div>
            <p v-if="block.item.desc && !block.item.img" class="news-desc">{{ block.item.desc }}</p>
            <p v-if="block.item.desc && block.item.img" class="news-desc"></p>
            <div class="news-meta">
              <span class="news-category">{{ getSource(block.item.source) }}</span>
              <span class="news-time">&nbsp;|&nbsp;{{ formatTime(block.item.time) }}</span>
              <div class="news-actions">
                <ShareButton :news-code="block.item.newsCode" :title="block.item.title" />
                <FavoriteButton :news-id="block.item.id" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载更多 / 没有更多 -->
    <div
      v-if="hasMore"
      class="load-more"
      @click="loadMore"
    >
      <img
        class="load-icon"
        referrerpolicy="no-referrer"
        :src="loadIcon"
      />
      <span v-if="!loading">点击加载更多</span>
      <span v-else>加载中...</span>
    </div>
    <div v-if="!hasMore && allNews.length > 0" class="no-more">
      <span>没有更多新闻数据</span>
    </div>
  </div>
</template>

<script>
import { fetchAdDetails, resolveAdImage, fetchNewsListL2, mapNewsItem } from '../../../api'
import FavoriteButton from './FavoriteButton.vue'
import ShareButton from './ShareButton.vue'

export default {
  name: 'ChannelThreeNews',
  components: { FavoriteButton, ShareButton },
  props: {
    categoryId: { type: [Number, String], default: null },
    excludeNewsIdList: { type: Array, default: () => [] }
  },
  data() {
    return {
      bannerIndex: 4,
      bannerImg: '',
      bannerLink: '',
      bannerShowTag: 0,
      loadIcon: require('../channel-three/img/FigmaDDSSlicePNGb212023b2c0c6c18e49d7afd472dbd20.png'),
      allNews: [],
      page: 1,
      pageSize: 10,
      hasMore: true,
      loading: false,
      DEFAULT_IMAGE: 'https://image.cbrand.com.cn/default/2026/10/AA38368160C540F19F19189B1D3A82AE.png'
    }
  },
  computed: {
    visibleNews() {
      return this.allNews
    },
    displayBlocks() {
      const blocks = []
      this.visibleNews.forEach((item, index) => {
        // 第 4 条后插入横幅，且只插入一次
        if (index === this.bannerIndex && this.visibleNews.length >= this.bannerIndex && this.bannerImg) {
          blocks.push({ type: 'banner', id: 'banner-' + index })
        }
        blocks.push({ type: 'news', id: item.newsCode, item })
      })
      return blocks
    }
  },
  watch: {
    categoryId() {
      // categoryId 变化时重置并加载
      this.page = 1
      this.allNews = []
      this.hasMore = true
      this.fetchNews()
    },
    excludeNewsIdList() {
      // 无论是否为空，都重新加载新闻列表（排除条件变化）
      this.page = 1
      this.allNews = []
      this.hasMore = true
      this.fetchNews()
    }
  },
  created() {
    // 页面创建时加载数据
    if (this.categoryId) {
      this.loadBanner()
      this.fetchNews()
    }
  },
  methods: {
    resetAndFetch() {
      this.page = 1
      this.allNews = []
      this.hasMore = true
      this.fetchNews()
    },
    async fetchNews() {
      if (this.loading || !this.categoryId) return
      this.loading = true
      try {
        const params = {
          categoryId: this.categoryId,
          pageNum: this.page,
          pageSize: this.pageSize
        }
        // 如果 excludeNewsIdList 有数据，添加到参数中
        if (this.excludeNewsIdList && this.excludeNewsIdList.length > 0) {
          params.excludeNewsIdList = this.excludeNewsIdList
          console.log('[ChannelThreeNews] excludeNewsIdList:', this.excludeNewsIdList, 'params:', params)
        } else {
          console.log('[ChannelThreeNews] excludeNewsIdList is empty:', this.excludeNewsIdList, 'params:', params)
        }
        const { list } = await fetchNewsListL2(params)
        const mapped = list.map(mapNewsItem)
        // 添加图片加载状态
        mapped.forEach(item => {
          item.loaded = false
          item.isLoading = true
          item.error = false
        })
        if (this.page === 1) {
          this.allNews = mapped
        } else {
          this.allNews = this.allNews.concat(mapped)
        }
        this.hasMore = mapped.length === this.pageSize
        this.page += 1
      } catch (e) {
        console.error('新闻加载失败', e)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.fetchNews()
    },
    formatTime(timeStr) {
      if (!timeStr) return timeStr
      const now = new Date()
      const time = new Date(timeStr)
      const diffMs = now.getTime() - time.getTime()
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffDays > 0) {
        return `${diffDays}天前`
      } else if (diffHours > 0) {
        return `${diffHours}小时前`
      } else if (diffMins > 0) {
        return `${diffMins}分钟前`
      } else {
        return '刚刚'
      }
    },
    goDetail(item) {
      if (item && item.newsCode) {
        window.open(`/zp/a/${item.newsCode}`, '_blank')
      }
    },
    getSource(source) {
      if (!source) return ''
      const truncated = source.split('|')[0]
      return this.truncateSevenChars(truncated)
    },
    truncateSevenChars(str) {
      if (!str) return ''
      if (str.length <= 7) return str
      return str.slice(0, 7) + '...'
    },
    goBannerLink() {
      if (!this.bannerLink) return
      window.open(this.bannerLink, '_blank')
    },
    async loadBanner() {
      const list = await fetchAdDetails('cate_t3_a_banner')
      const ad = (list || [])[0] || {}
      if (ad.imageUrl) {
        this.bannerImg = resolveAdImage(ad.imageUrl)
      }
      this.bannerLink = ad.turnUrl || ''
      this.bannerShowTag = ad.isShowTag || 0
    },
    handleImageError(event, originalUrl) {
      console.warn(`图片加载失败：${originalUrl}，切换为默认图片`)
      event.target.src = this.DEFAULT_IMAGE
    },
    handleImageLoad(itemId) {
      const item = this.allNews.find(i => i.id === itemId)
      if (item) {
        item.loaded = true
        item.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.channel-three-news {
  width: 685px;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.news-item {
  width: 685px;
  height: 178px;
  background-color: rgba(245, 245, 247, 1);
  display: flex;
  overflow: hidden;
  transition: all 0.2s ease;
}

.news-thumb,
.news-title {
  cursor: pointer;
}

.news-item:hover {
  background-color: rgba(255, 255, 255, 1);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.news-thumb {
  width: 317px;
  height: 177px;
  object-fit: cover;
  flex-shrink: 0;
  display: block;
  position: relative;
  transition: opacity 0.3s ease;
}

/* 加载中状态 */
.img-loading {
  opacity: 0.3;
}

/* 图片占位符 - 居中显示 */
.img-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  color: #999;
  z-index: 1;
}

.news-content {
  flex: 1;
  min-width: 0;
  padding: 12px 15px 0 25px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.no-image .news-content {
  padding: 14px 24px 2px 25px;
}
.no-image .news-desc {
  margin-top: -25px;
    height: 56px;
}
.news-title {
  font-size: 18px;
  color: #333;
  line-height: 32px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: color 0.2s;
}

.news-item:hover .news-title {
  color: rgba(0, 0, 0, 1);
}

.no-image .news-title {
  color: rgba(51, 51, 51, 1);
  -webkit-line-clamp: 1;
}

.news-desc {
  font-size: 14px;
  color: #666;
  line-height: 28px;
  margin: 9px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.news-meta {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin: 12px 0 12px;
}

.news-category,
.news-time {
  color: rgba(102, 102, 102, 1);
}

.news-actions {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.action-item {
  display: flex;
  align-items: center;
  margin-left: 25px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.action-item:hover {
  color: #C00513;
}

.action-icon {
  width: 14px;
  height: 14px;
  margin-right: 4px;
  object-fit: contain;
}

/* 横幅 */
.banner-wrap {
  width: 685px;
  height: 120px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
}

.ad-tag {
  position: absolute;
  top: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 0 0 0 4px;
  line-height: 1.5;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 加载更多 */
.load-more {
  width: 155px;
  height: 34px;
  margin: 32px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: rgba(102, 102, 102, 1);
  transition: color 0.2s;
}

.load-more:hover {
  color: rgba(192, 5, 19, 1);
}

.load-icon {
  width: 24px;
  height: 25px;
  margin-right: 6px;
  object-fit: contain;
}

/* 没有更多 */
.no-more {
  margin: 32px auto 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
